import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { EmailVerification } from '@feathr/blackbox';
import type { ITanStackColumnMeta } from '@feathr/components';
import { Address, Pill, TanStackHeader } from '@feathr/components';

import EmailOptions from './EmailOptions';
import { getStatusLabel, getStatusTheme, hasAddress } from './EmailsPage.utils';

type TCell = CellContext<EmailVerification, unknown>;

/** Each sortable column `id` is the field the list route orders by. */
function emailsColumns(t: TFunction): Array<ColumnDef<EmailVerification>> {
  return [
    {
      accessorFn: (row): string => row.get('email'),
      cell: ({ row }: TCell): JSX.Element => (
        <Observer>{(): JSX.Element => <>{row.original.get('email')}</>}</Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Primary email')} />,
      id: 'email',
      meta: { isFlex: true, isPrimary: true } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      accessorFn: (row): string => row.get('status'),
      cell: ({ row }: TCell): JSX.Element => (
        <Observer>
          {(): JSX.Element => {
            const status = row.original.get('status');
            return (
              <Pill isLoading={row.original.isPending} theme={getStatusTheme(status)}>
                {getStatusLabel(status, t)}
              </Pill>
            );
          }}
        </Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Status')} />,
      id: 'status',
      sortDescFirst: false,
      size: 100,
    },
    {
      accessorFn: (row): string => row.get('from_name') ?? '',
      cell: ({ row }: TCell): JSX.Element => (
        <Observer>{(): JSX.Element => <>{row.original.get('from_name')}</>}</Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Name')} />,
      id: 'from_name',
      meta: { isFlex: true } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      cell: ({ row }: TCell): JSX.Element => (
        <Observer>
          {(): JSX.Element => {
            const address = row.original.get('address');
            return hasAddress(address) ? <Address {...address} /> : <>-</>;
          }}
        </Observer>
      ),
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Address')} />,
      id: 'address',
      meta: { isFlex: true } satisfies ITanStackColumnMeta,
    },
    {
      cell: ({ row }: TCell): JSX.Element => <EmailOptions email={row.original} />,
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Options')} />,
      id: 'options',
      meta: { align: 'center', pinned: 'right' } satisfies ITanStackColumnMeta,
      size: 80,
    },
  ];
}

export default emailsColumns;
